import { ArrowRight, Download, Trash2 } from "lucide-react";

import { Modal, PrimaryCommandButton } from "@/ui";

export type ParentConfirmation =
  | { kind: "export"; profileId: string; profileName: string }
  | { kind: "delete"; profileId: string; step: 1 | 2 }
  | { kind: "clear"; step: 1 | 2 };

export interface ParentConfirmationDialogProps {
  confirmation?: ParentConfirmation;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ParentConfirmationDialog({
  confirmation,
  busy,
  onCancel,
  onConfirm
}: ParentConfirmationDialogProps) {
  if (confirmation === undefined) {
    return null;
  }

  const isExport = confirmation.kind === "export";
  const isFinalStep = !isExport && confirmation.step === 2;
  const isClearAll = confirmation.kind === "clear";
  const title = getConfirmationTitle(confirmation);
  const finalLabel = isClearAll ? "全部清空" : "永久删除";

  return (
    <Modal
      actions={
        <>
          <button
            className="parent-center__secondary-command"
            disabled={busy}
            onClick={onCancel}
            type="button"
          >
            取消
          </button>
          <PrimaryCommandButton
            className={
              isFinalStep ? "parent-center__destructive-command" : undefined
            }
            disabled={busy}
            icon={isExport ? Download : isFinalStep ? Trash2 : ArrowRight}
            iconPosition="end"
            onClick={onConfirm}
          >
            {busy
              ? isExport
                ? "正在导出"
                : "请稍候"
              : isExport
                ? "确认导出"
                : isFinalStep
                  ? finalLabel
                  : "继续确认"}
          </PrimaryCommandButton>
        </>
      }
      closeLabel={isExport ? "关闭导出确认" : "取消"}
      onClose={onCancel}
      open
      title={title}
    >
      <p>{getConfirmationMessage(confirmation)}</p>
      {isExport ? (
        busy ? (
          <p className="parent-center__confirmation-step" role="status">
            正在准备 JSON 文件，请稍候。
          </p>
        ) : null
      ) : (
        <p className="parent-center__confirmation-step">
          第 {confirmation.step} 次确认，共 2 次
        </p>
      )}
    </Modal>
  );
}

function getConfirmationTitle(confirmation: ParentConfirmation): string {
  if (confirmation.kind === "export") {
    return `确认导出 ${confirmation.profileName} 的资料`;
  }

  if (confirmation.kind === "delete") {
    return confirmation.step === 1 ? "确认删除资料" : "最后确认删除";
  }

  return confirmation.step === 1 ? "确认清空全部资料" : "最后确认清空";
}

function getConfirmationMessage(confirmation: ParentConfirmation): string {
  if (confirmation.kind === "export") {
    return `将导出“${confirmation.profileName}”的小船员资料。JSON 文件只会保存在这台设备上，除非家长主动分享，否则不会离开这台设备。`;
  }

  if (confirmation.kind === "delete") {
    return confirmation.step === 1
      ? "删除后，这个资料的练习记录、掌握情况和装饰会一并移除。"
      : "删除后无法恢复。请再次确认是否永久删除这个资料。";
  }

  return confirmation.step === 1
    ? "所有小船员资料、练习记录、掌握情况和装饰都会被移除。"
    : "清空后无法恢复。请再次确认是否删除设备上的全部资料。";
}
